import {
  injectDynatraceSnippet,
  endDynatraceSession,
  trackDynatraceSession,
  trackDynatraceAction,
  trackDynatraceError,
  trackDynatraceUser,
  initializeDynatrace,
  disableDynatrace,
  enableDynatrace,
} from "./dynatrace";
import { fetchDynatraceSnippet } from "./serverDynatrace";

// Fetch snippet function exposed for use in app api routes
export { fetchDynatraceSnippet };

const DYNATRACE_ENABLE_RETRY_DELAY_MS = 250;
const DYNATRACE_ENABLE_MAX_ATTEMPTS = 20;

class RumProvider {
  private isInitializing = false;
  private isDisabled = false;
  private enableRetryTimeout: ReturnType<typeof setTimeout> | undefined;
  // Incremented on disable() so stale async initializations cannot re-enable tracking.
  private initializationGeneration = 0;
  private trackActionInternal: (
    name: string,
    properties?: Record<string, unknown>,
  ) => void = () => {};
  private trackSessionInternal: (properties: Record<string, unknown>) => void =
    () => {};
  private trackErrorInternal: (
    error: unknown,
    properties?: Record<string, unknown>,
  ) => void = () => {};
  private trackUserInternal: (id: string) => void = () => {};
  private endSessionInternal: () => void = () => {};

  // --- Flow tracking ---
  private enableFlowTracking = false;
  private currentFlow: string = "unknown";
  private sessionContext: Record<string, unknown> = {};

  private readonly setinternalMethods: () => void = () => {
    this.trackActionInternal = trackDynatraceAction;
    this.trackSessionInternal = trackDynatraceSession;
    this.trackErrorInternal = trackDynatraceError;
    this.trackUserInternal = trackDynatraceUser;
    this.endSessionInternal = endDynatraceSession;
  };

  private readonly resetInternalMethods: () => void = () => {
    this.trackActionInternal = () => {};
    this.trackSessionInternal = () => {};
    this.trackErrorInternal = () => {};
    this.trackUserInternal = () => {};
    this.endSessionInternal = () => {};
  };

  private addFlowIfEnabled(properties?: Record<string, unknown>) {
    if (!this.enableFlowTracking) return properties;
    return {
      ...properties,
      flow: properties?.flow ?? this.currentFlow,
    };
  }

  private clearEnableRetryTimeout() {
    if (this.enableRetryTimeout) {
      clearTimeout(this.enableRetryTimeout);
      this.enableRetryTimeout = undefined;
    }
  }

  private enableDynatraceWhenReady(
    initGeneration = this.initializationGeneration,
    attempt = 0,
  ) {
    if (this.isDisabled || initGeneration !== this.initializationGeneration) {
      return;
    }

    const enabled = enableDynatrace();
    if (enabled || attempt >= DYNATRACE_ENABLE_MAX_ATTEMPTS) {
      this.clearEnableRetryTimeout();
      return;
    }

    this.clearEnableRetryTimeout();
    this.enableRetryTimeout = setTimeout(() => {
      this.enableRetryTimeout = undefined;
      this.enableDynatraceWhenReady(initGeneration, attempt + 1);
    }, DYNATRACE_ENABLE_RETRY_DELAY_MS);
  }

  /**
   * Initializes Dynatrace by fetching and injecting the snippet with SRI
   * we use the route created in every app to fetch the snippet without exposing our dynatrace api token
   *
   * Initializes Dynatrace with snippet obtained via API
   *
   * @param options.enableFlowTracking  Active send of `flow`
   * @param options.flowValue           Initial value of `flow`
   * @returns Promise<boolean> True if initialization succeeded
   */
  async initializeFromApi(options?: {
    enableFlowTracking?: boolean;
    flowValue?: string;
  }): Promise<boolean> {
    if (this.isDisabled || this.isInitializing) {
      return false;
    }

    this.isInitializing = true;
    const initGeneration = this.initializationGeneration;
    const isCurrentInitialization = () =>
      !this.isDisabled && initGeneration === this.initializationGeneration;

    try {
      const response = await fetch("/api/rum-snippet");

      if (!isCurrentInitialization()) {
        return false;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch snippet: ${response.status}`);
      }
      const snippet = await response.text();

      if (!isCurrentInitialization()) {
        return false;
      }

      const initialized = injectDynatraceSnippet(snippet);
      if (!initialized || !isCurrentInitialization()) {
        return false;
      }

      // --- Activate flow tracking if configured ---
      if (options?.enableFlowTracking) {
        this.enableFlowTracking = true;
        if (options.flowValue) {
          this.currentFlow = options.flowValue;
        }
      }

      this.setinternalMethods();
      this.enableDynatraceWhenReady(initGeneration);
      return true;
    } catch (e) {
      return false;
    } finally {
      if (initGeneration === this.initializationGeneration) {
        this.isInitializing = false;
      }
    }
  }

  /**
   * Initializes Dynatrace by injecting the snippet from the dynatrace url
   * this method of initialization is used for apps that dont require SRI
   * @returns boolean True if initialization succeeded
   */
  initialize(): boolean {
    if (this.isDisabled) {
      return false;
    }

    const initialized = initializeDynatrace();
    if (!initialized) {
      return false;
    }
    this.setinternalMethods();
    this.enableDynatraceWhenReady();
    return true;
  }

  trackAction(name: string, properties?: Record<string, unknown>) {
    const merged = { ...this.sessionContext, ...properties };
    this.trackActionInternal(name, this.addFlowIfEnabled(merged));
  }

  trackSession(properties: Record<string, unknown>) {
    this.sessionContext = { ...this.sessionContext, ...properties };
    this.trackSessionInternal(this.addFlowIfEnabled(properties) ?? {});
  }

  trackError(error: unknown, properties?: Record<string, unknown>) {
    this.trackErrorInternal(error, this.addFlowIfEnabled(properties));
  }

  trackUser(id: string) {
    this.trackUserInternal(id);
  }

  endSession() {
    this.endSessionInternal();
  }

  /**
   * Best-effort disable for consent revocation:
   * - ends current session if Dynatrace is active
   * - resets all internal tracking methods to no-op
   * - clears flow tracking state
   */
  disable() {
    this.initializationGeneration += 1;
    this.isDisabled = true;
    this.isInitializing = false;
    this.clearEnableRetryTimeout();
    this.endSessionInternal();
    disableDynatrace();
    this.resetInternalMethods();
    this.enableFlowTracking = false;
    this.currentFlow = "unknown";
    this.sessionContext = {};
  }

  enable() {
    this.isDisabled = false;
    this.enableDynatraceWhenReady();
  }
}

// Due to the way ES6 modules resolve, this will be a singleton by default
// There must only one be instance so the correct internal methods are always called
export const rumProvider = new RumProvider();
export type RumProviderType = typeof rumProvider;
